import React, { useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import { calculateEmissionsPurchasedElectricity } from '../../Utils/moduleCalculates';
import { EmissionsPurchasedElectricity } from '../../../../../../services/types/CalculatorTransportRoads';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';

interface Props {
  emissionsPurchasedElectricity: EmissionsPurchasedElectricity;
  onUpdateEmissionsPurchasedElectricity: (
    updatedEmissionsPurchasedElectricity: EmissionsPurchasedElectricity
  ) => void;
}

const EmissionsPurchasedElectricitySection: React.FC<Props> = ({
  emissionsPurchasedElectricity,
  onUpdateEmissionsPurchasedElectricity,
}) => {
  const { t } = useTranslation();

  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(
    emissionsPurchasedElectricity.subTotal
  );
  const isInputDisabled = true;
  const inputEnergyRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const amountElectricityConsumed = Number(
      emissionsPurchasedElectricity.amountElectricityConsumed
    );
    const { countryFactor } = emissionsPurchasedElectricity;

    if (amountElectricityConsumed && countryFactor !== null) {
      const result = calculateEmissionsPurchasedElectricity(
        amountElectricityConsumed,
        countryFactor
      );

      onUpdateEmissionsPurchasedElectricity({
        ...emissionsPurchasedElectricity,
        amountElectricityConsumed,
        countryName: emissionsPurchasedElectricity.countryName,
        countryFactor,
        moduleId: 0,
        subTotal: result,
      });

      setTotalCarbonSum(result);
    } else {
      setTotalCarbonSum(null);
      onUpdateEmissionsPurchasedElectricity({
        ...emissionsPurchasedElectricity,
        amountElectricityConsumed: emissionsPurchasedElectricity.amountElectricityConsumed,
        countryName: emissionsPurchasedElectricity.countryName,
        countryFactor: emissionsPurchasedElectricity.countryFactor,
        moduleId: emissionsPurchasedElectricity.moduleId,
        subTotal: null,
      });
    }
  }, [
    emissionsPurchasedElectricity.amountElectricityConsumed,
    emissionsPurchasedElectricity.countryName,
    emissionsPurchasedElectricity.countryFactor,
    emissionsPurchasedElectricity.moduleId,
    emissionsPurchasedElectricity.subTotal,
  ]);

  const handleInputamountElectricityConsumed = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateEmissionsPurchasedElectricity({
      ...emissionsPurchasedElectricity,
      amountElectricityConsumed: value === '' ? null : Number(value),
    });
  };

  return (
    <>
      {isInputDisabled && (
        <>
          <div className="row calculator-input-row">
            <div className="col-12 col-sm-5 col-md-3 col-lg-3">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Country Selected')}
              </label>
            </div>
            <div className="col-12 col-sm-5 col-md-4 col-lg-2">
              <input
                title="Country Selected"
                type="text"
                id="countryNameInput"
                value={
                  emissionsPurchasedElectricity.countryName !== null
                    ? emissionsPurchasedElectricity.countryName
                    : ''
                }
                className="calculator-input"
                disabled={isInputDisabled}
              />
            </div>
          </div>
          <div className="row calculator-input-row">
            <div className="col-12 col-sm-5 col-md-3 col-lg-3">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Electricity Emission Factor')}
              </label>
            </div>
            <div className="col-12 col-sm-5 col-md-4 col-lg-2">
              <input
                title="Electricity Emission Factor"
                type="text"
                id="countryFactorInput"
                value={
                  emissionsPurchasedElectricity.countryFactor !== null
                    ? emissionsPurchasedElectricity.countryFactor
                    : ''
                }
                className="calculator-input"
                disabled={isInputDisabled}
              />
            </div>
          </div>
        </>
      )}

      <div className="row calculator-input-row">
        <div className="col-12 col-sm-5 col-md-3 col-lg-3">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Amount of Electricity Consumed')}
          </label>
        </div>
        <div className="col-12 col-sm-5 col-md-4 col-lg-2">
          <input
            ref={inputEnergyRef}
            type="number"
            id="wattageInput"
            value={
              emissionsPurchasedElectricity.amountElectricityConsumed !== null
                ? emissionsPurchasedElectricity.amountElectricityConsumed
                : ''
            }
            min="0"
            max={99999999.99}
            maxLength={10}
            className="calculator-input"
            onChange={handleInputamountElectricityConsumed}
            title="Amount of Electricity Consumed"
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered  action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          {totalCarbonSum !== null && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                {/* espaciador intencional, sin contenido */}
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td className="action-column text-right" style={{ height: '46.9271px' }} />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
    </>
  );
};

export default EmissionsPurchasedElectricitySection;
